import { useMemo } from 'react';

import { parseInflationPercent } from '@/utils/inflationParser';
import { Quote } from '@client/model/quotesModel';
import { enrichQuotesWithROI, type EnrichedQuote } from '@shared';
import { adjustQuotesForPremium } from '@client/util/quoteAdjustment';
import { parseDurationYears as parseBenefitPeriod } from '@client/utils/benefitPeriod';
import { isEqual } from 'lodash';

type FilterName = 'premium' | 'inflationProtection' | 'benefitDuration';

interface CombinedFilterType {
  type: 'combined' | 'separate';
  combined: number[];
  aob: number[];
  cob: number[];
}

interface UseAdjustedQuotesParams {
  selectedPremium: number;
  client: Client | undefined;
  selectedTab: ShopInsuranceTab;
  originalQuotes: Quote[] | undefined;
  selectedBenefitDuration: CombinedFilterType;
  selectedInflationProtection: CombinedFilterType;
  returnOnInvestmentCalculationType: ReturnOnInvestmentCalculationType;
  selectedFeatureBenefitsFilters?: string[];
}

interface UseAdjustedQuotesReturn {
  isAdjusted: FilterName[];
  originalTotalQuotes: number;
  adjustedQuotes: EnrichedQuote[];
}

const PREMIUM_ADJUSTMENT_THRESHOLD = 100;
const MAX_DURATION_YEARS = 8;

const DEFAULT_FILTERS_VALUES = {
  benefitDuration: [1, MAX_DURATION_YEARS],
  inflationProtection: [0, 5],
};

export const DEFAULT_FILTERS_STATE = {
  benefitDuration: {
    type: 'combined' as const,
    combined: DEFAULT_FILTERS_VALUES.benefitDuration,
    aob: DEFAULT_FILTERS_VALUES.benefitDuration,
    cob: DEFAULT_FILTERS_VALUES.benefitDuration,
  },
  inflationProtection: {
    type: 'combined' as const,
    combined: DEFAULT_FILTERS_VALUES.inflationProtection,
    aob: DEFAULT_FILTERS_VALUES.inflationProtection,
    cob: DEFAULT_FILTERS_VALUES.inflationProtection,
  },
};

/**
 * Checks if a numeric value falls within a min-max range.
 *
 * Special case: Infinity (representing "Lifetime" duration) is treated as
 * within range if the max value equals the slider's maximum setting.
 * This allows users to include/exclude Lifetime products by adjusting the slider.
 */
function inRange(value: number, min: number, max: number, sliderMax?: number): boolean {
  // Handle Lifetime (Infinity) values:
  // Include them only when the slider is at its maximum position
  if (value === Infinity) {
    return sliderMax !== undefined && max >= sliderMax;
  }

  return value >= min && value <= max;
}

function isFilterApplied(
  filter: CombinedFilterType | null | undefined,
  defaultState: CombinedFilterType,
) {
  return !!filter && !isEqual(filter, defaultState);
}

function shouldAdjustPremium(quotes: Quote[], selectedPremium: number) {
  return quotes.some(
    quote => Math.abs(quote.premiumAmount - selectedPremium) > PREMIUM_ADJUSTMENT_THRESHOLD,
  );
}

function parseDurationYearsAsNumber(value?: string): number {
  const result = parseBenefitPeriod(value);
  return result === 'lifetime' ? Infinity : result;
}

/**
 * Some carriers set a single inflation string (e.g. "3% Compound") via `inflationBenefitOption`.
 * Others separate AOB/COB via `aobInflation`/`cobInflation`.
 * This normalizer yields aob/cob numbers where possible and the single-rate when applicable.
 */
function getInflationProtection(quote: Quote) {
  const hasSingle = !!quote.inflationBenefitOption && quote.inflationBenefitOption !== 'None';

  if (hasSingle) {
    const rate = parseInflationPercent(quote.inflationBenefitOption);
    return { hasSingle: true, single: rate, aob: rate, cob: rate };
  }

  return {
    hasSingle: false,
    single: undefined as number | undefined,
    aob: parseInflationPercent(quote.aobInflation),
    cob: parseInflationPercent(quote.cobInflation),
  };
}

function getBenefitsDuration(quote: Quote) {
  const aobYears = parseDurationYearsAsNumber(quote.aobDuration);
  const cobYears = parseDurationYearsAsNumber(quote.cobDuration);

  if (aobYears || cobYears) {
    const totalYears =
      aobYears === Infinity || cobYears === Infinity ? Infinity : aobYears + cobYears;
    return { aobYears, cobYears, totalYears };
  }

  const totalYears = (quote.ltcBenefitDurationMonths ?? 0) / 12;

  return { aobYears: 0, cobYears: 0, totalYears };
}

function matchesTab(quote: Quote, tab: ShopInsuranceTab) {
  if (tab === 'joint') {
    return quote.policyType === 'Joint Life';
  }
  if (tab === 'couples') {
    return quote.policyType === 'Couples Discount';
  }
  // For single tab, accept both 'Single Life' and Lincoln 'Standard'
  return quote.policyType === 'Single Life' || quote.policyType === 'Standard';
}

function matchesInflationProtection(quote: Quote, filter: CombinedFilterType) {
  const { hasSingle, single, aob, cob } = getInflationProtection(quote);

  if (filter.type === 'separate') {
    const [minA, maxA] = filter.aob;
    const [minC, maxC] = filter.cob;

    if (hasSingle && typeof single === 'number') {
      return inRange(single, minA, maxA) && inRange(single, minC, maxC);
    }

    return inRange(aob, minA, maxA) && inRange(cob, minC, maxC);
  }

  const [min, max] = filter.combined;

  if (hasSingle && typeof single === 'number') {
    return inRange(single, min, max);
  }

  const isHybrid = aob !== cob;

  if (isHybrid) return inRange(aob, min, max) || inRange(cob, min, max);

  return inRange(aob, min, max);
}

function matchesBenefitsDuration(quote: Quote, filter: CombinedFilterType) {
  const { aobYears, cobYears, totalYears } = getBenefitsDuration(quote);

  if (filter.type === 'separate') {
    const [minA, maxA] = filter.aob;
    const [minC, maxC] = filter.cob;

    if (aobYears || cobYears) {
      return (
        inRange(aobYears, minA, maxA, MAX_DURATION_YEARS) &&
        inRange(cobYears, minC, maxC, MAX_DURATION_YEARS)
      );
    }

    const minTotal = minA + minC;
    const maxTotal = maxA + maxC;

    return inRange(totalYears, minTotal, maxTotal, MAX_DURATION_YEARS * 2);
  }

  const [minT, maxT] = filter.combined;

  if (aobYears || cobYears) {
    return inRange(aobYears + cobYears, minT, maxT, MAX_DURATION_YEARS);
  }

  return inRange(totalYears, minT, maxT, MAX_DURATION_YEARS);
}

function matchesFeatureBenefits(quote: Quote, selectedFilters: string[]): boolean {
  if (selectedFilters.length === 0) return true;

  return selectedFilters.some(filter => {
    if (filter === 'hasGlobalBenefits') return quote.hasGlobalBenefits;
    return false;
  });
}

function buildAppliedFilters(
  inflationFilter: CombinedFilterType,
  durationFilter: CombinedFilterType,
  filtered: Quote[],
  selectedPremium: number,
): FilterName[] {
  const applied: FilterName[] = [];

  if (isFilterApplied(inflationFilter, DEFAULT_FILTERS_STATE.inflationProtection)) {
    applied.push('inflationProtection');
  }

  if (isFilterApplied(durationFilter, DEFAULT_FILTERS_STATE.benefitDuration)) {
    applied.push('benefitDuration');
  }

  if (shouldAdjustPremium(filtered, selectedPremium)) {
    applied.push('premium');
  }
  return applied;
}

function useAdjustedQuotes({
  client,
  selectedTab,
  originalQuotes,
  selectedPremium,
  selectedBenefitDuration,
  selectedInflationProtection,
  returnOnInvestmentCalculationType,
  selectedFeatureBenefitsFilters = [],
}: UseAdjustedQuotesParams): UseAdjustedQuotesReturn {
  return useMemo(() => {
    if (!originalQuotes?.length || !client) {
      return { adjustedQuotes: [], isAdjusted: [], originalTotalQuotes: 0 };
    }

    const filteredQuotes = originalQuotes.filter(
      q =>
        matchesTab(q, selectedTab) &&
        matchesInflationProtection(q, selectedInflationProtection) &&
        matchesBenefitsDuration(q, selectedBenefitDuration) &&
        matchesFeatureBenefits(q, selectedFeatureBenefitsFilters),
    );

    if (!filteredQuotes.length) {
      return { adjustedQuotes: [], isAdjusted: [], originalTotalQuotes: originalQuotes.length };
    }

    const appliedFilters = buildAppliedFilters(
      selectedInflationProtection,
      selectedBenefitDuration,
      filteredQuotes,
      selectedPremium,
    );

    const quotesToProcess = appliedFilters.length
      ? adjustQuotesForPremium(filteredQuotes, selectedPremium)
      : filteredQuotes;

    // Enrich quotes with ROI calculations using shared utility
    const withROI = enrichQuotesWithROI(
      quotesToProcess,
      client,
      returnOnInvestmentCalculationType,
      {
        includeOriginalROIValues: true,
      },
    );

    const adjustedQuotes = withROI
      .filter(q => (q.projectedCostCoverage ?? 0) > 0)
      .sort((a, b) => (b.roi ?? 0) - (a.roi ?? 0));

    return {
      adjustedQuotes,
      isAdjusted: appliedFilters,
      originalTotalQuotes: originalQuotes.length,
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    client,
    selectedTab,
    originalQuotes,
    selectedPremium,
    returnOnInvestmentCalculationType,
    selectedBenefitDuration.type,
    selectedBenefitDuration.combined,
    selectedBenefitDuration.aob,
    selectedBenefitDuration.cob,
    selectedInflationProtection.type,
    selectedInflationProtection.combined,
    selectedInflationProtection.aob,
    selectedInflationProtection.cob,
    selectedFeatureBenefitsFilters,
  ]);
}

export { DEFAULT_FILTERS_VALUES, useAdjustedQuotes, type CombinedFilterType, type FilterName };
